import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "../../../../components/Button";
import Input from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import TextArea from "../../../../components/TextArea";
import Switch from "../../../../components/Switch";
import { TRANSACTION_STATUS, VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { Validation } from "../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../interfaces/transaction.interface";

interface FormProps {
  view?: boolean;
  edit?: boolean;
  formData: AddEditInterface;
  validation: Validation;
  dropdownSearchData: any;
  allProperties: any[];
  properties: any[];
  allVendors: any[];
  vendors: any[];
  allPaidByOptions?: any[];
  paidByOptions?: any[];
  allReservations: any[];
  reservations?: any[];
  allTransactionTypes: any[];
  transactionTypes: any[];
  allHstOptions: any[];
  hstOptions: any[];
  allReviewedByOptions: any[];
  reviewedByOptions?: any[];
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const Form: React.FC<FormProps> = ({
  formData,
  view,
  edit,
  validation,
  dropdownSearchData,
  allProperties,
  properties,
  allVendors,
  vendors,
  allPaidByOptions,
  paidByOptions,
  allReservations,
  reservations,
  allTransactionTypes,
  transactionTypes,
  allHstOptions,
  hstOptions,
  allReviewedByOptions,
  reviewedByOptions,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
}) => {
  const { t } = useTranslation();
  
  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t('transactions.addEdit.pageTexts.form.sections.primaryDetails')}
        </h6>

        <div className="col-md-3 col-lg-2">
          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              required
              id={VALIDATION_FIELD_MAPPING.transactionDate}
              name={VALIDATION_FIELD_MAPPING.transactionDate}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.transactionDate)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.transactionDate)?.message ?? ''}
              label={t('transactions.addEdit.pageTexts.form.transactionDate.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.transactionDate.placeholder')}
              value={formData.transactionDate}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.transactionDate)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.transactionDate)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.propertyId ? [formData.propertyId] : []}
              required
              options={(properties || []).map((property) => ({
                label: property.name,
                value: property.id
              }))}
              allOptions={(allProperties || []).map((property) => ({
                label: property.name,
                value: property.id
              }))}
              id={VALIDATION_FIELD_MAPPING.propertyId}
              name={VALIDATION_FIELD_MAPPING.propertyId}
              disabled={view || edit}
              searchText={dropdownSearchData.propertyId}
              onSearch={onDropdownSearchChange}
              placeholder={t('transactions.addEdit.pageTexts.form.property.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.property.label')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.propertyId)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.propertyId)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.transactionType ? [formData.transactionType] : []}
              required
              options={(transactionTypes || []).map((transactionType) => ({
                label: transactionType.name,
                value: transactionType.id
              }))}
              allOptions={(allTransactionTypes || []).map((transactionType) => ({
                label: transactionType.name,
                value: transactionType.id
              }))}
              id={VALIDATION_FIELD_MAPPING.transactionType}
              name={VALIDATION_FIELD_MAPPING.transactionType}
              disabled={view}
              searchText={dropdownSearchData.transactionType}
              placeholder={t('transactions.addEdit.pageTexts.form.transactionType.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.transactionType.label')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.transactionType)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.transactionType)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <div className="form-control btn-group w-100 p-0">
              <Button
                className={
                  clsx(
                    "btn btn-sm h-100 flex-fill shadow-none border-0 text-uppercase fw-semibold",
                    formData.debitCredit === 'dr' && 'btn-primary',
                    formData.debitCredit !== 'dr' && 'text-primary'
                  )
                }
                text={t('transactions.addEdit.pageTexts.form.debitCredit.debit')}
                disabled={view}
                onClick={() => onInputChange({ target: { value: 'dr' } }, VALIDATION_FIELD_MAPPING.debitCredit)}
              />
              <Button
                className={
                  clsx(
                    "btn btn-sm h-100 flex-fill shadow-none border-0 text-uppercase fw-semibold",
                    formData.debitCredit === 'cr' && 'btn-success',
                    formData.debitCredit !== 'cr' && 'text-primary'
                  )
                }
                text={t('transactions.addEdit.pageTexts.form.debitCredit.credit')}
                disabled={view}
                onClick={() => onInputChange({ target: { value: 'cr' } }, VALIDATION_FIELD_MAPPING.debitCredit)}
              />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.reservationId ? [formData.reservationId] : []}
              options={(formData.propertyId ? (reservations || []) : []).map((reservation: any) => ({
                label: reservation.name,
                value: reservation.id
              }))}
              allOptions={(formData.propertyId ? (allReservations || []) : []).map((reservation: any) => ({
                label: reservation.name,
                value: reservation.id
              }))}
              id={VALIDATION_FIELD_MAPPING.reservationId}
              name={VALIDATION_FIELD_MAPPING.reservationId}
              disabled={view}
              searchText={dropdownSearchData.reservationId}
              placeholder={t('transactions.addEdit.pageTexts.form.reservation.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.reservation.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.vendorId ? [formData.vendorId] : []}
              options={(vendors || []).map((vendor) => ({
                label: vendor.name,
                value: vendor.id
              }))}
              allOptions={(allVendors || []).map((vendor) => ({
                label: vendor.name,
                value: vendor.id
              }))}
              id={VALIDATION_FIELD_MAPPING.vendorId}
              name={VALIDATION_FIELD_MAPPING.vendorId}
              disabled={view}
              searchText={dropdownSearchData.vendorId}
              placeholder={t('transactions.addEdit.pageTexts.form.vendor.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.vendor.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.paidBy ? [formData.paidBy] : []}
              options={(paidByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              allOptions={(allPaidByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              id={VALIDATION_FIELD_MAPPING.paidBy}
              name={VALIDATION_FIELD_MAPPING.paidBy}
              disabled={view}
              searchText={dropdownSearchData.paidBy}
              placeholder={t('transactions.addEdit.pageTexts.form.paidBy.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.paidBy.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo}
              name={VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo}
              disabled={view}
              label={t('transactions.addEdit.pageTexts.form.carryforwardInvoiceNo.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.carryforwardInvoiceNo.placeholder')}
              value={formData.carryforwardInvoiceNo}
              error={!!validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo)}
              errorMessage={validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo)?.message ?? ""}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.carryforwardInvoiceNo)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          2. {t('transactions.addEdit.pageTexts.form.sections.transactionDetails')}
        </h6>

        <div className="col-md-3 col-lg-2">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              disabled={view}
              required
              value={formData.subTotal}
              id={VALIDATION_FIELD_MAPPING.subTotal}
              name={VALIDATION_FIELD_MAPPING.subTotal}
              label={t('transactions.addEdit.pageTexts.form.subTotal.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.subTotal.placeholder')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.subTotal)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.subTotal)?.message ?? ''}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.subTotal)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.subTotal)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.hst ? [formData.hst] : []}
              noSearch
              disabled={view}
              options={(hstOptions || []).map((item) => ({
                label: String(item.primary_tax_percentage),
                value: String(item.primary_tax_percentage)
              }))}
              allOptions={(allHstOptions || []).map((item) => ({
                label: String(item.primary_tax_percentage),
                value: String(item.primary_tax_percentage)
              }))}
              id={VALIDATION_FIELD_MAPPING.hst}
              name={VALIDATION_FIELD_MAPPING.hst}
              placeholder={t('transactions.addEdit.pageTexts.form.hst.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.hst.label')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hst)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hst)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              disabled
              id={VALIDATION_FIELD_MAPPING.hstAmount}
              name={VALIDATION_FIELD_MAPPING.hstAmount}
              label={t('transactions.addEdit.pageTexts.form.hstAmount.label')}
              value={formData.hstAmount}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              disabled={view}
              value={formData.totalAmount}
              id={VALIDATION_FIELD_MAPPING.totalAmount}
              name={VALIDATION_FIELD_MAPPING.totalAmount}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.totalAmount)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.totalAmount)?.message ?? ''}
              label={t('transactions.addEdit.pageTexts.form.totalAmount.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.totalAmount.placeholder')}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.totalAmount)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.totalAmount)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              disabled={view}
              value={formData.actualCost}
              id={VALIDATION_FIELD_MAPPING.actualCost}
              name={VALIDATION_FIELD_MAPPING.actualCost}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.actualCost)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.actualCost)?.message ?? ''}
              label={t('transactions.addEdit.pageTexts.form.actualCost.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.actualCost.placeholder')}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.actualCost)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.actualCost)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>

        <div className="col-md-10">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.expenseDetails}
              name={VALIDATION_FIELD_MAPPING.expenseDetails}
              label={t('transactions.addEdit.pageTexts.form.expenseDetails.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.expenseDetails.placeholder')}
              value={formData.expenseDetails}
              rows={4}
              disabled={view}
              required
              className="form-control-lg h-100"
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.expenseDetails)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.expenseDetails)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.expenseDetails)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.expenseDetails)}
            />
          </div>
        </div>

        <div className="col-md-3 col-lg-2">
          <div className="form-floating form-floating-outline w-100 d-flex align-items-start">
            <Switch
              label={t('transactions.addEdit.pageTexts.form.chargeable.label')}
              disabled={view || formData.status !== TRANSACTION_STATUS.unbilled}
              name={VALIDATION_FIELD_MAPPING.chargeable}
              checked={!!formData.chargeable}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.chargeable)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          3. {t('transactions.addEdit.pageTexts.form.sections.otherDetails')}
        </h6>

        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              label={t('transactions.addEdit.pageTexts.form.notes.label')}
              placeholder={t('transactions.addEdit.pageTexts.form.notes.placeholder')}
              value={formData.notes}
              disabled={view}
              required={!formData.reviewed}
              rows={4}
              className="form-control-lg h-100"
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline mb-6">
            <Switch
              label={t('transactions.addEdit.pageTexts.form.reviewed.label')}
              name={VALIDATION_FIELD_MAPPING.reviewed}
              checked={!!formData.reviewed}
              disabled={view}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.reviewed)}
            />
          </div>

          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.reviewedBy ? [formData.reviewedBy] : []}
              disabled={view}
              required={!formData.reviewed}
              options={(reviewedByOptions || []).map((item: any) => ({ 
                label: item.name, 
                value: item.id 
              }))}
              allOptions={(allReviewedByOptions || []).map((item: any) => ({ 
                label: item.name, 
                value: item.id 
              }))}
              id={VALIDATION_FIELD_MAPPING.reviewedBy}
              name={VALIDATION_FIELD_MAPPING.reviewedBy}
              searchText={dropdownSearchData.reviewedBy}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)?.message ?? ''}
              placeholder={t('transactions.addEdit.pageTexts.form.reviewedBy.placeholder')}
              label={t('transactions.addEdit.pageTexts.form.reviewedBy.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default Form;
